import ReactDOM from 'react-dom/client';
import { init } from '@telegram-apps/sdk-react';
import '@telegram-apps/telegram-ui/dist/styles.css';
import { initSentry } from './sentry';
import App from './App';
import { NotInTelegram } from './components/NotInTelegram';

// Init Sentry IMMEDIATELY on module load — before anything else runs.
// Sentry installs global window.onerror / unhandledrejection handlers,
// so even errors thrown during Telegram SDK init() are captured.
// No-op if VITE_SENTRY_DSN is unset (local dev w/o .env.local).
initSentry();

/**
 * Boot strategy:
 *
 *   1. URL-based Telegram detection. If the page URL contains tgWebAppData
 *      (in hash or query), we're inside the Telegram WebView → init SDK and
 *      render the full app.
 *
 *   2. If not in Telegram AND `?dev=1` is present, load the mock so a
 *      developer can preview the UI in Chrome. API calls still 401 (mock
 *      hash isn't valid against the real bot), but layout/styles render.
 *
 *   3. Otherwise (browser, no flag) → render the "Open in Telegram" wall.
 *
 *   4. If init() throws (e.g. stale localStorage from a previous dev session
 *      or any unexpected env issue) we fall back to the wall too — keeps the
 *      browser UX clean.
 *
 * Why URL-only (not retrieveLaunchParams()): the SDK caches launch params in
 * localStorage. Once dev=1 is used, the mock leaves data behind, and a later
 * visit without ?dev=1 would falsely detect "in Telegram" via cache → init()
 * fails because no real bridge. URL-only detection is stateless and robust.
 */
async function bootstrap() {
  const root = ReactDOM.createRoot(document.getElementById('root')!);

  // Coming Soon mode: pure static render — skip Telegram detection, mock,
  // and SDK init. The placeholder must render anywhere (browser, Telegram,
  // search engine preview) without side effects.
  if (import.meta.env.VITE_COMING_SOON === 'true') {
    root.render(<App />);
    return;
  }

  const isDevPreview = new URLSearchParams(window.location.search).get('dev') === '1';
  const isTelegram = detectTelegramFromUrl();

  if (!isTelegram && !isDevPreview) {
    root.render(<NotInTelegram />);
    return;
  }

  if (!isTelegram && isDevPreview) {
    await import('./mock-env');
  }

  try {
    init({ acceptCustomStyles: true });
  } catch (e) {
    console.error('[boot] SDK init failed', e);
    if (!isDevPreview) {
      root.render(<NotInTelegram />);
      return;
    }
    // In dev preview we tolerate a partial init — UI can still render even
    // if some SDK methods (mainButton, viewport) are unhappy.
  }

  root.render(<App />);
}

function detectTelegramFromUrl(): boolean {
  const { hash, search } = window.location;
  return hash.includes('tgWebAppData') || search.includes('tgWebAppData');
}

bootstrap();
